import { Input } from "@/components/ui/input";

export type TableControlOption = { label: string; value: string };

export type TableSelectControl = {
  id: string;
  label: string;
  value: string;
  options: TableControlOption[];
  allLabel?: string;
  includeAll?: boolean;
};

/**
 * The in-tab filter row for this module's own tables.
 *
 * NOT the shell `FilterBar`, and the difference is a real one rather than a
 * preference: `FilterBar` drives URL search params so a filtered view is a
 * shareable link, and it is the right control for a tab whose filters ARE the
 * view. These filters are local narrowing inside a work queue — a sort key, a
 * text match while scanning — and pushing every keystroke into the portal's URL
 * would fill a reader's history with one entry per character typed.
 *
 * The select is a native `<select>` for the same reason `FilterBar`'s is: the
 * portal ships no shadcn Select primitive, and hand-rolling a Radix listbox
 * inside a module is exactly the second component vocabulary the shell rules
 * exist to prevent.
 */
export function TableControls({
  selects,
  textValue,
  textPlaceholder = "Filter text",
  onSelectChange,
  onTextChange,
  resultCount,
  totalCount,
  countNoun = "items",
  showText = true,
}: {
  selects: TableSelectControl[];
  textValue: string;
  textPlaceholder?: string;
  onSelectChange: (id: string, value: string) => void;
  onTextChange: (value: string) => void;
  resultCount?: number;
  totalCount?: number;
  countNoun?: string;
  showText?: boolean;
}) {
  return (
    <div role="search" className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      {selects.map((select) => (
        <label key={select.id} className="grid min-w-36 flex-1 grid-cols-[minmax(0,1fr)] gap-1.5 sm:max-w-52">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {select.label}
          </span>
          <select
            className="min-h-9 w-full min-w-0 truncate rounded-ks-sm border border-ks-grey-3 bg-ks-surface px-2 text-[13px] text-ks-ink"
            aria-label={select.label}
            value={select.value}
            onChange={(event) => onSelectChange(select.id, event.target.value)}
          >
            {select.includeAll === false ? null : (
              <option value="">{select.allLabel ?? `All ${select.label.toLowerCase()}`}</option>
            )}
            {select.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ))}
      {showText && (
        <label className="grid min-w-52 flex-[2] grid-cols-[minmax(0,1fr)] gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Text</span>
          <Input
            className="min-h-9"
            type="search"
            value={textValue}
            placeholder={textPlaceholder}
            onChange={(event) => onTextChange(event.target.value)}
          />
        </label>
      )}
      {typeof resultCount === "number" && (
        // The count is stated as "N of M" whenever the total is known, because
        // "12 shown" cannot tell a reader whether a filter is hiding 3 rows or 3,000.
        <p className="self-end pb-2 text-xs tabular-nums text-muted-foreground">
          {typeof totalCount === "number"
            ? `${resultCount.toLocaleString("en-US")} of ${totalCount.toLocaleString("en-US")} ${countNoun}`
            : `${resultCount.toLocaleString("en-US")} shown`}
        </p>
      )}
    </div>
  );
}
